import { useState, useCallback, useEffect } from 'react';
import {
  Group,
  Button,
  Text,
  TextInput,
  Select,
  Stack,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  Plus,
  Search,
  Download,
} from 'react-feather';
import type { CohortWithDetails, ResourceWithDetails, ResourceCategory } from '../../../types';
import { resourcesApi } from '../../../services/api/resources';
import { ConfigurableTable, ExportModal } from '../../../components/common';
import { renderResourceCellContent, renderResourceActions } from './ResourceCellRenderers';
import { RESOURCE_DEFAULT_COLUMNS, getResourceColumnMinWidth } from './resourceColumnConfig';
import { AddResourceModal } from './AddResourceModal';
import { EditResourceModal } from './EditResourceModal';

interface ResourcesTabProps {
  cohort: CohortWithDetails;
  isActive?: boolean;
}

export function ResourcesTab({ cohort, isActive }: ResourcesTabProps) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [sessionFilter, setSessionFilter] = useState<string | null>(null);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [resources, setResources] = useState<ResourceWithDetails[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);
  const [selectedResource, setSelectedResource] = useState<ResourceWithDetails | null>(null);

  // Fetch resources
  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const result = await resourcesApi.getResourcesForCohort(cohort.id, {
        search: search || undefined,
        category_id: categoryFilter || undefined,
        session_ids: sessionFilter ? [sessionFilter] : undefined,
      });
      setResources(result.data);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to fetch resources',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohort.id, search, categoryFilter, sessionFilter]);

  // Only fetch when tab becomes active
  useEffect(() => {
    if (isActive) {
      resourcesApi.getCategories().then((cats) => {
        setCategories(cats);
      }).catch((error) => {
        console.error('Failed to fetch categories:', error);
      });
      fetchResources();
    }
  }, [isActive, fetchResources]);

  const refresh = () => fetchResources();

  // Handle edit
  const handleEdit = (resource: ResourceWithDetails) => {
    setSelectedResource(resource);
    openEditModal();
  };

  // Handle delete
  const handleDelete = (resource: ResourceWithDetails) => {
    modals.openConfirmModal({
      title: 'Delete Resource',
      children: (
        <Text size="sm">
          Are you sure you want to delete <strong>{resource.title}</strong>? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await resourcesApi.delete(resource.id);
          notifications.show({
            title: 'Success',
            message: 'Resource deleted successfully',
            color: 'green',
          });
          refresh();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to delete resource',
            color: 'red',
          });
        }
      },
    });
  };

  // Handle hide evergreen
  const handleHideEvergreen = async (resource: ResourceWithDetails) => {
    try {
      await resourcesApi.hideEvergreenFromCohort(cohort.id, resource.id);
      notifications.show({
        title: 'Success',
        message: 'Resource hidden from this cohort',
        color: 'green',
      });
      refresh();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to hide resource',
        color: 'red',
      });
    }
  };

  // Handle download/open
  const handleOpen = (resource: ResourceWithDetails) => {
    if (resource.resource_type === 'link' && resource.url) {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    } else if (resource.download_url) {
      window.open(resource.download_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Session options for filter
  const sessionOptions = (cohort.sessions || [])
    .filter((s) => s.session_type === 'group_session' || s.session_type === 'workshop_session')
    .map((s) => ({
      value: s.id,
      label: s.session_type === 'workshop_session'
        ? `Workshop${s.session_number ? ` ${s.session_number}` : ''}: ${s.title || 'Untitled'}`
        : s.title || `Session ${s.session_number}`,
    }));

  // Category options for filter
  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const hasActiveFilters = !!(search || categoryFilter || sessionFilter);

  return (
    <>
      <Stack gap="md">
        {/* Toolbar: search & filters left, actions right */}
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            <TextInput
              placeholder="Search resources..."
              leftSection={<Search size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={280}
            />
            <Select
              placeholder="All categories"
              data={categoryOptions}
              value={categoryFilter}
              onChange={setCategoryFilter}
              clearable
              style={{ width: 180 }}
            />
            <Select
              placeholder="All sessions"
              data={sessionOptions}
              value={sessionFilter}
              onChange={setSessionFilter}
              clearable
              style={{ width: 200 }}
            />
          </Group>
          <Group gap="sm">
            <Button variant="outline" color="gray" bg="white" style={{ borderColor: '#E8E6E3' }} leftSection={<Download size={16} />} onClick={openExportModal}>
              Export
            </Button>
            <Button leftSection={<Plus size={16} />} onClick={openAddModal}>
              Add Resource
            </Button>
          </Group>
        </Group>

        {/* Resources table */}
        <ConfigurableTable<ResourceWithDetails>
          data={resources}
          columns={RESOURCE_DEFAULT_COLUMNS}
          getRowKey={(r) => r.id}
          renderCell={(resource, column) => renderResourceCellContent(column, resource)}
          renderActions={(resource) =>
            renderResourceActions(resource, {
              onOpen: handleOpen,
              onEdit: handleEdit,
              onDelete: handleDelete,
              onHideEvergreen: handleHideEvergreen,
            })
          }
          sortConfig={null}
          onSort={() => {}}
          loading={loading}
          page={1}
          pageSize={resources.length || 1}
          totalCount={resources.length}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
          visibleColumnCount={RESOURCE_DEFAULT_COLUMNS.length}
          emptyStateContent={
            <Text c="dimmed" ta="center" py="xl">
              No resources found. Add a resource to get started.
            </Text>
          }
          hasActiveFilters={hasActiveFilters}
          onClearFilters={() => {
            setSearch('');
            setCategoryFilter(null);
            setSessionFilter(null);
          }}
          getColumnMinWidth={getResourceColumnMinWidth}
          actionsColumnWidth={80}
          entityLabel="resources"
        />
      </Stack>

      {/* Add Resource Modal */}
      <AddResourceModal
        opened={addModalOpened}
        onClose={closeAddModal}
        cohortId={cohort.id}
        sessions={cohort.sessions || []}
        categories={categories}
        onCreated={refresh}
      />

      {/* Edit Resource Modal */}
      {selectedResource && (
        <EditResourceModal
          opened={editModalOpened}
          onClose={() => {
            closeEditModal();
            setSelectedResource(null);
          }}
          resource={selectedResource}
          sessions={cohort.sessions || []}
          categories={categories}
          onUpdated={refresh}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        entityType="resources"
        contextType="cohort"
        contextId={cohort.id}
        // No count: the list shows evergreen resources, and the file holds only this cohort's own.
        filterContext={{ search: search || undefined, category_id: categoryFilter || undefined }}
        filterSummary={[
          ...(search ? [{ label: 'Search', value: search }] : []),
          ...(categoryFilter ? [{ label: 'Category', value: categories.find(c => c.id === categoryFilter)?.name || categoryFilter }] : []),
        ]}
        ignoredFilters={sessionFilter ? ['Session'] : undefined}
      />
    </>
  );
}
